import { Mail } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

type EmailInputFieldDebounceProps = {
  label?: string;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  errorMessage?: string;
  className?: string;
  name?: string;
  rightIcon?: React.ReactNode;
  autoFocus?: boolean;
  required?: boolean;
  delay?: number;
  testId?: string;
};

const EmailInputFieldDebounce = ({
  label,
  value,
  onChange,
  placeholder,
  disabled,
  errorMessage,
  className,
  name,
  rightIcon,
  autoFocus,
  required,
  delay = 400,
  testId,
}: EmailInputFieldDebounceProps) => {
  const { t } = useTranslation();
  const [internalValue, setInternalValue] = useState(value || "");
  const [isInvalidEmail, setIsInvalidEmail] = useState(false);

  useEffect(() => {
    setInternalValue(value || "");
  }, [value]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (internalValue !== (value || "")) {
        if (onChange) onChange(internalValue);
      }
      const email = (internalValue || "").trim().toLowerCase();
      if (email && !/\S+@\S+\.\S+/.test(email)) {
        setIsInvalidEmail(true);
      } else {
        setIsInvalidEmail(false);
      }
    }, delay);
    return () => clearTimeout(handler);
  }, [internalValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBlur = () => {
    if (onChange) onChange(internalValue);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      if (onChange) onChange(internalValue);
    }
  };

  return (
    <div className={`w-full ${className || ""}`}>
      {label ? (
        <label className="block text-sm font-semibold text-gray-800 mb-2">
          {label}
          {required ? <span className="text-red-600"> *</span> : null}
        </label>
      ) : null}
      <div className="relative">
        <span
          className={`absolute left-3 top-1/2 -translate-y-1/2 ${
            isInvalidEmail ? "text-red-500" : "text-gray-500"
          } pointer-events-none`}
        >
          <Mail className="w-5 h-5" />
        </span>
        <input
          data-testid={testId}
          autoFocus={autoFocus}
          type="text"
          name={name}
          value={internalValue}
          onChange={(e) => setInternalValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={placeholder}
          className={`w-full pl-10 ${
            rightIcon ? "pr-10" : ""
          } px-3 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-800 placeholder:text-gray-500 focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-600 disabled:bg-gray-50 disabled:text-gray-400`}
        />
        {rightIcon ? (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none">
            {rightIcon}
          </span>
        ) : null}
      </div>
      {isInvalidEmail ? (
        <p className="mt-1 text-xs text-red-600">{t("Invalid email format")}</p>
      ) : null}
      {!isInvalidEmail && errorMessage ? (
        <p className="mt-1 text-xs text-red-600">{errorMessage}</p>
      ) : null}
    </div>
  );
};

export default EmailInputFieldDebounce;
